import { TriangleDay } from "../../models/triangle-day";
import { useContext, useEffect, useRef, useState } from "react";
import { TriangleChart } from "../../components/ui/triangle-chart";
import { Location as TQSLocation } from "../../models/location";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { format, subDays } from "date-fns";
import { generateToken } from "../../reducers/sessionSlice";
import { useAppDispatch, useAppSelector } from "../../reducers/store";
import { environment } from "../../environments/environment";
import { getQATriangleData as getQATriangleDataAction, getProactiveAudits as getProactiveAuditsAction, getWeeklyQaData as getWeeklyQaDataAction } from "../../reducers/qualityAlertSlice";

export function KpiSection(props: {
  selectedLocation: TQSLocation;
  allLocations: TQSLocation[];
}) {
  interface SelectOption {
    value: string;
    label: string;
  }

  const dispatch = useAppDispatch();
  const selectedSupplier = useAppSelector((state) => state.supplier.selectedSupplier);
  const selectedProducingFacilities = useAppSelector((state) => state.supplier.selectedProducingFacilities);

  const [options, setOptions] = useState<SelectOption[]>([]);
  const [selectedDate, setDate] = useState(
    new Date().getFullYear() + "-" + (new Date().getMonth() + 1),
  );
  const [selectedDateLabel, setLabel] = useState(
    new Date().toLocaleString("en-us", { month: "short", year: "numeric" }),
  );
  const [alerts, setAlerts] = useState<TriangleDay[] | null[]>([]);
  const [qualityIssues, setQualityIssues] = useState<any>(null);
  const [yearQIs, setYearQI] = useState<any[]>();
  const [totalQualityIssues, setTotalQualityIssues] = useState<number>(0);
  const [totalNCTs, setTotalNCTs] = useState<number>(0);
  const [proactiveAuditData, setProactiveAuditData] = useState<any>({});
  const [isLoading, setIsloading] = useState(false); // QA triangle data, separate from KPI loading due to the month selector in the triangle chart
  const [isLoadingKPI, setIsLoadingKPI] = useState(false); // KPI data, separate from QA triangle loading due to the month selector in the triangle chart
  const [generatingReport, setGeneratingReport] = useState(false);
  // const [proactiveAudits90Days, setproactiveAudits90Days] = useState<number>(0);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    setTimeout(() => {
      getWeek(signal);
      getYearlyQaData(signal);
      getProactiveData();
    }, 0);

    return () => {
      controller.abort();
    };
    // getpast90DaysProactiveData()
  }, [selectedSupplier, props.allLocations, props.selectedLocation]);

  useEffect(() => {
    setTimeout(() => {
      getMonths();
      getQATriangleData();
    }, 0);
  }, [selectedDate, selectedSupplier, props.allLocations, props.selectedLocation]);

  async function getQATriangleData() {
    setIsloading(true);
    try {
      const dateLabel = selectedDate;
      const locationId = props.selectedLocation?.id;
      
      const result = await dispatch(getQATriangleDataAction({
        dateFilter: dateLabel,
        supplierId: selectedSupplier?.id?.toString() ?? '',
        signal: new AbortController().signal,
        locationId: locationId
      }));
      
      if (result.payload) {
        setAlerts(result.payload as TriangleDay[]);
      }
    } catch (error) {
      console.error("Error fetching QA triangle data:", error);
    } finally {
      setIsloading(false);
    }
  }

  async function handleViewReport() {
    if (generatingReport || !selectedSupplier?.id) {
      return;
    }

    setGeneratingReport(true);
    const customerPortalUrl = environment.customerDashboardUrl;

    try {
      const tokenResult = await dispatch(generateToken(selectedSupplier.id));
      
      if (tokenResult.payload) {
        const secretKey = tokenResult.payload.token;
        const embeddedLink = `${customerPortalUrl}/#${encodeURIComponent(secretKey)}`;
        window.open(embeddedLink, "_blank");
      } else {
        throw new Error('Failed to generate token');
      }
    } catch (error) {
      console.error("Error generating token or opening report:", error);
    } finally {
      setGeneratingReport(false);
    }
  }

  async function getYearlyQaData(signal: AbortSignal) {
    if (!selectedSupplier?.id) return;

    setIsLoadingKPI(true);
    try {
      const todaysDate = new Date();
      const lastYear = new Date();
      lastYear.setFullYear(todaysDate.getFullYear() - 1);

      const locationId = props.selectedLocation?.id;

      const result = await dispatch(getWeeklyQaDataAction({
        end: lastYear.toUTCString(),
        start: todaysDate.toUTCString(),
        supplierId: selectedSupplier.id.toString(),
        signal: signal,
        locationId: locationId
      }));
      
      if (result.payload) {
        setYearQI(result.payload);
      }
    } catch (error) {
      console.error("Error fetching yearly QA data:", error);
    } finally {
      setIsLoadingKPI(false);
    }
  }

  function getTotalNCTAvoidences() {
    let totalNCTAvoidences = 0;
    yearQIs?.map((qi) => {
      totalNCTAvoidences += qi.totalNearMisses;
    });
    return totalNCTAvoidences;
  }

  function getNCTSavings() {
    const totalamount = getTotalNCTAvoidences() * 2474;
    return (
      "$" + totalamount.toFixed(0).replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,")
    );
  }

  function getMonths() {
    const months: SelectOption[] = [];
    const today = new Date();

    for (let i = 0; i < 12; i++) {
        const date = new Date(today.getFullYear(), today.getMonth() - i, 1);

        const value = `${date.getFullYear()}-${date.getMonth() + 1}`;
        const label = date.toLocaleString("en-US", {
            month: "short",
            year: "numeric",
        }); // e.g., "Apr 2025"

        months.push({ value, label });
    }

    setOptions(months);
}

  function onDateChange(selectedDate: any) {
    setIsloading(true);
    setDate(selectedDate.value);
    setLabel(selectedDate.label);
  }

  function getDayOfWeek(date: string) {
    const dayOfweek = new Date(date).getUTCDay();
    switch (dayOfweek) {
      case 0:
        return "S";
      case 1:
        return "M";
      case 2:
        return "T";
      case 3:
        return "W";
      case 4:
        return "T";
      case 5:
        return "F";
      case 6:
        return "S";
    }
  }

  async function getProactiveData() {
    if (!selectedSupplier?.id) return;

    setIsLoadingKPI(true);
    try {
      const currentDay = new Date();
      const lastWeek = new Date();
      lastWeek.setUTCDate(currentDay.getUTCDate() - 7);
      
      const locationId = props.selectedLocation?.id;

      const result = await dispatch(getProactiveAuditsAction({
        end: currentDay.toUTCString(),
        start: lastWeek.toUTCString(),
        supplierId: selectedSupplier.id.toString(),
        locationId: locationId
      }));

      if (result.payload) {
        setProactiveAuditData(result.payload);
      }
    } catch (error) {
      console.error("Error fetching proactive audit data:", error);
    } finally {
      setIsLoadingKPI(false);
    }
  }

  async function getWeek(signal: AbortSignal) {
    if (!selectedSupplier?.id) return;

    setIsLoadingKPI(true);
    try {
      const week = [];
      const curr = new Date();
      // Get Monday as the first day of the week
      const first = curr.getDate() - curr.getDay() + (curr.getDay() === 0 ? -6 : 1);
      for (let i = 0; i < 7; i++) {
        const setDate = new Date(curr);
        setDate.setDate(first + i);
        const year = setDate.getUTCFullYear();
        const month =
          setDate.getUTCMonth() + 1 < 10
            ? `0${setDate.getUTCMonth() + 1}`
            : setDate.getUTCMonth() + 1;
        const day =
          setDate.getUTCDate() < 10
            ? `0${setDate.getUTCDate()}`
            : setDate.getUTCDate();

        week.push(`${year}-${month}-${day}`);
      }

      const locationId = props.selectedLocation?.id;

      const [weekInfoResult, lastSevenDaysResult] = await Promise.all([
        dispatch(getWeeklyQaDataAction({
          end: week[0],
          start: week[6],
          supplierId: selectedSupplier.id.toString(),
          signal: signal,
          locationId: locationId
        })),
        dispatch(getWeeklyQaDataAction({
          end: format(subDays(new Date(), 6), "yyyy-MM-dd"),
          start: format(new Date(), "yyyy-MM-dd"),
          supplierId: selectedSupplier.id.toString(),
          signal: signal,
          locationId: locationId
        }))
      ]);

      if (weekInfoResult.payload && lastSevenDaysResult.payload) {
        setQualityIssues(weekInfoResult.payload);

        let totalIssues = 0;
        for (let dayInfo of weekInfoResult.payload) {
          totalIssues += dayInfo?.totalQualityAlerts ?? 0;
        }

        let totalNCTs = 0;
        for (let dayInfo of lastSevenDaysResult.payload) {
          totalNCTs += dayInfo?.totalNearMisses ?? 0;
        }

        setTotalNCTs(totalNCTs);
        setTotalQualityIssues(totalIssues);
      }
    } catch (error) {
      console.error("Error fetching week data:", error);
    } finally {
      setIsLoadingKPI(false);
    }
  }

  function getDaysInMonth(month: number) {
    switch (month) {
      case 1:
        return 31;
      case 2:
        return 28;
      case 3:
        return 31;
      case 4:
        return 30;
      case 5:
        return 31;
      case 6:
        return 30;
      case 7:
        return 31;
      case 8:
        return 31;
      case 9:
        return 30;
      case 10:
        return 31;
      case 11:
        return 30;
      case 12:
        return 31;
      default:
        return 0;
    }
  }

  return (
    <div className="flex max-h-[100%] w-full gap-[14px]">
      <div className="h-full w-[250px] min-w-[250px] max-w-[250px] overflow-visible rounded-[8px] border-[1px] border-[#E5E5E5] bg-[#f6f6f6] p-2">
        <div className="flex items-center p-1">
          <h4 className="w-[50%] text-[14px] font-semibold">
            {selectedDateLabel}
          </h4>
          <Select
            className="z-50 h-[30px] w-[55%] text-[12px]"
            value={options.find((option) => option.value === selectedDate)}
            defaultValue={options[0]}
            options={options}
            styles={{
              control: (base) => ({
                ...base,
                height: 30,
                minHeight: 30,
                maxHeight: 30,
              }),
              indicatorsContainer: (base) => ({
                ...base,
                height: 30,
                minHeight: 30,
                maxHeight: 30,
              }),
              valueContainer: (base) => ({
                ...base,
                height: 30,
                minHeight: 30,
                maxHeight: 30,
              }),
            }}
            onChange={(d) => onDateChange(d)}
          />
        </div>
        <div className="flex justify-center">
          {isLoading ? (
            <div className="flex h-[200px] w-full items-center justify-center">
              <div className="loading loading-spinner text-tqsYellow"></div>
            </div>
          ) : (
            <TriangleChart days={alerts} />
          )}
        </div>
      </div>

      <div className="flex w-full gap-[14px]">
        <div className="flex h-full w-1/3 flex-col rounded-[8px] border-[1px] border-[#E5E5E5] bg-[#f6f6f6] p-2">
          <div className="h-[calc(100%-34px)] w-full overflow-auto p-2">
            {isLoadingKPI ? (
              <div className="flex h-full w-full items-center justify-center">
                <div className="loading loading-spinner text-tqsYellow"></div>
              </div>
            ) : (
              <>
                <h3 className="text-[14px] font-semibold text-[#8e8e93]">
                  Quality Issues
                </h3>
                <p className="text-[12px] font-medium text-[#d4d5d7] opacity-0">
                  Last 7d
                </p>
                <h1 className="mb-[10px] text-[25px] font-bold text-tqsBlack">
                  {totalQualityIssues}
                </h1>
                <div className="flex justify-around text-center">
                  {qualityIssues?.length > 0 &&
                    qualityIssues.map((qi: any, i: number) => {
                  return (
                    <div key={i}>
                      <h6 className="text-tqsBlack">{qi.totalQualityAlerts}</h6>
                      {new Date(qi.date) > new Date() && (
                        <div
                          className={`triangle flex h-[11.25px] w-[13.5px] items-end justify-center bg-[#e2e2e2]`}
                        ></div>
                      )}
                      {qi.totalQualityAlerts > 0 &&
                        !(new Date(qi.date) > new Date()) && (
                          <div
                            className={`triangle flex h-[11.25px] w-[13.5px] items-end justify-center bg-[#ff443a]`}
                          ></div>
                        )}
                      {qi.totalQualityAlerts == 0 &&
                        qi.totalNearMisses > 0 &&
                        !(new Date(qi.date) > new Date()) && (
                          <div
                            className={`triangle flex h-[11.25px] w-[13.5px] items-end justify-center bg-[#fedd00]`}
                          ></div>
                        )}
                      {qi.totalQualityAlerts == 0 &&
                        qi.totalNearMisses == 0 &&
                        !(new Date(qi.date) > new Date()) && (
                          <div
                            className={`triangle flex h-[11.25px] w-[13.5px] items-end justify-center bg-tqsGreen`}
                          ></div>
                        )}
                      <h6 className="text-[12px] text-tqsBlack">
                        {getDayOfWeek(qi.date)}
                      </h6>
                    </div>
                  );
                })}
            </div>
              </>
            )}
          </div>
          <div className="h-[34px] w-full overflow-hidden text-ellipsis text-nowrap border-t-[1px] border-t-[#E5E5E5] p-[6px]">
            <a
              className="text-[14px] text-[#8e8e93]"
              onClick={handleViewReport}
              style={{ cursor: "pointer" }}
            >
              View Report
              <FontAwesomeIcon
                className="pl-[10px] text-[10px]"
                icon={faChevronRight}
              />
            </a>
          </div>
        </div>

        <div className="h-full w-1/3 overflow-hidden rounded-[8px] border-[1px] border-[#E5E5E5] bg-[#f6f6f6] p-2">
          <div className="h-[calc(100%-34px)] w-full overflow-auto p-2">
            {isLoadingKPI ? (
              <div className="flex h-full w-full items-center justify-center">
                <div className="loading loading-spinner text-tqsYellow"></div>
              </div>
            ) : (
              <>
                <h3 className="text-[14px] font-semibold text-[#8e8e93]">
                  NCTs Avoided
                </h3>
                <p className="text-[12px] font-medium text-[#d4d5d7]">Last 7d</p>
                <h1 className="mb-[10px] text-[25px] font-bold text-tqsBlack">
                  {totalNCTs ?? 0}
                </h1>
                <div className="flex flex-col justify-around space-y-2 text-center">
                  <div className="flex w-full flex-col justify-between">
                    <span className="text-left text-sm text-tqsBlack">
                      {getTotalNCTAvoidences() ?? 0}
                    </span>
                    <span className="w-full text-left text-[10px] text-[#8e8e93]">
                      last 365 days
                    </span>
                  </div>
                  <div className="flex w-full flex-col justify-between">
                    <span className="w-full max-w-[50%] text-left text-sm text-tqsBlack">
                      {getNCTSavings() ?? 0}
                    </span>{" "}
                    <span className="w-full text-left text-[10px] text-[#8e8e93]">
                      saved in NCT prevention est.
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="h-[34px] w-full overflow-hidden text-ellipsis text-nowrap border-t-[1px] border-t-[#E5E5E5] p-[6px]">
            <a
              onClick={handleViewReport}
              className="text-[14px] text-[#8e8e93]"
              style={{ cursor: "pointer" }}
            >
              View Report{" "}
              <FontAwesomeIcon
                className="pl-[10px] text-[10px]"
                icon={faChevronRight}
              />
            </a>
          </div>
        </div>

        <div className="h-full w-1/3 overflow-hidden rounded-[8px] border-[1px] border-[#E5E5E5] bg-[#f6f6f6] p-2">
          <div className="h-[calc(100%-34px)] w-full overflow-auto p-2">
            {isLoadingKPI ? (
              <div className="flex h-full w-full items-center justify-center">
                <div className="loading loading-spinner text-tqsYellow"></div>
              </div>
            ) : (
              <>
                <h3 className="text-[14px] font-semibold text-[#8e8e93]">
                  Proactive Audits
                </h3>
                <p className="text-[12px] font-medium text-[#d4d5d7]">Last 7d</p>
                <h1 className="mb-[10px] text-[25px] font-bold text-tqsBlack">
                  {proactiveAuditData.proactiveAuditsPastWeek}
                </h1>
                <div className="flex flex-wrap justify-around text-center">
                  <div className="flex w-full justify-between">
                    <span className="w-1/3 text-left text-sm text-tqsBlack">
                      {proactiveAuditData.proactiveAuditsPastYear ?? 0}
                    </span>{" "}
                    <span className="w-1/3 text-left text-[10px] text-[#8e8e93]">
                      last 365 days
                    </span>
                  </div>
                  <div className="flex w-full justify-between">
                    <span className="w-1/3 text-left text-sm text-tqsBlack">
                      {proactiveAuditData.proactiveAuditsPastThreeMonth ?? 0}
                    </span>{" "}
                    <span className="w-1/3 text-left text-[10px] text-[#8e8e93]">
                      last 90 days
                    </span>
                  </div>
                  <div className="flex w-full justify-between">
                    <span className="w-1/3 text-left text-sm text-tqsBlack">
                      {proactiveAuditData.proactiveAuditsPastMonth ?? 0}
                    </span>{" "}
                    <span className="w-1/3 text-left text-[10px] text-[#8e8e93]">
                      last 30 days
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="h-[34px] w-full overflow-hidden text-ellipsis text-nowrap border-t-[1px] border-t-[#E5E5E5] p-[6px]">
            <a
              className="text-[14px] text-[#8e8e93]"
              onClick={handleViewReport}
              style={{ cursor: "pointer" }}
            >
              View Report{" "}
              <FontAwesomeIcon
                className="pl-[10px] text-[10px]"
                icon={faChevronRight}
              />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
